/**
 * How far the client got: each step of the intake as she sees it, whether
 * her saved progress (or her submission) answers it, and where she stopped.
 * Conditional steps her answers ruled out are "skipped", not "unanswered";
 * ones whose deciding answer isn't in yet still count as open.
 */
import type {
  OutgoingIntakeAnswer,
  OutgoingIntakeFormNode
} from "@/api/types/outgoingIntake";
import { isNodeVisible } from "@/features/foreignNationalOutgoingIntakes/conditional";
import { flattenIntakeStepNodes } from "@/features/foreignNationalOutgoingIntakes/intakeStepFlattening";

export type TrackStepState = "answered" | "stopped" | "open" | "skipped";

export type TrackStep = {
  node: OutgoingIntakeFormNode;
  n: number | null;
  label: string;
  upload: boolean;
  state: TrackStepState;
};

export type TrackProgress = {
  steps: TrackStep[];
  answered: number;
  total: number;
};

const text = (v: unknown) =>
  v === null || v === undefined ? "" : String(v).trim();

const hasValue = (a: OutgoingIntakeAnswer) => {
  if (a.files?.length) return true;
  const v = a.value;
  if (v === null || v === undefined || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object")
    return Object.values(v).some((x) => text(x) !== "");
  return true;
};

const questionIds = (node: OutgoingIntakeFormNode): string[] =>
  node.node_type === "question"
    ? [text(node.node_id)]
    : (node.children ?? []).flatMap(questionIds);

export const trackProgress = (
  schema: OutgoingIntakeFormNode[],
  answers: OutgoingIntakeAnswer[],
  stillWorking: boolean
): TrackProgress => {
  const answered = new Set<string>();
  const groups = new Set<string>();
  const values: Record<string, unknown> = {};
  for (const a of answers.filter(hasValue)) {
    const group = text(a.repeatable_group_id);
    if (group) groups.add(group);
    else {
      answered.add(text(a.question_id));
      values[text(a.question_id)] = a.value;
    }
  }

  const steps: TrackStep[] = [];
  let n = 0;
  let stopped = false;
  for (const node of flattenIntakeStepNodes(schema)) {
    const label = text(node.prompt ?? node.title) || "Untitled";
    const upload =
      node.node_type === "question"
        ? node.question_type === "file"
        : (node.children ?? []).some((c) => c.question_type === "file");
    const rule = node.show_if?.question_id;
    if (rule && rule in values && !isNodeVisible(node, values)) {
      steps.push({ node, n: null, label, upload, state: "skipped" });
    } else {
      n += 1;
      const done =
        node.node_type === "repeatable_group"
          ? groups.has(text(node.node_id))
          : questionIds(node).some((id) => answered.has(id));
      let state: TrackStepState = done ? "answered" : "open";
      if (!done && stillWorking && !stopped && answers.length) {
        state = "stopped";
        stopped = true;
      }
      steps.push({ node, n, label, upload, state });
    }
  }
  const shown = steps.filter((s) => s.state !== "skipped");
  return {
    steps,
    answered: shown.filter((s) => s.state === "answered").length,
    total: shown.length
  };
};
